'use strict';

const DEFAULT_TARGET_BYTES = 1572864;
const DEFAULT_MAX_DIMENSION = 2000;
const WEBP_QUALITY_STEPS = [0.82, 0.74, 0.66, 0.58];

function webpFileName(name) {
    const base = String(name || 'zdjecie').replace(/\.[^.]+$/, '').trim() || 'zdjecie';
    return `${base}.webp`;
}

function isHeicFile(file) {
    const type = String(file && file.type || '').toLowerCase();
    const name = String(file && file.name || '').toLowerCase();
    return type === 'image/heic' || type === 'image/heif' || /\.(heic|heif)$/.test(name);
}

function isSupportedImageFile(file) {
    const type = String(file && file.type || '').toLowerCase();
    const name = String(file && file.name || '').toLowerCase();
    return ['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif'].includes(type)
        || /\.(jpe?g|png|webp|heic|heif)$/.test(name);
}

function loadImage(file) {
    return new Promise((resolve, reject) => {
        const url = URL.createObjectURL(file);
        const image = new Image();
        image.onload = () => resolve({
            source: image,
            width: image.naturalWidth || image.width,
            height: image.naturalHeight || image.height,
            cleanup: () => URL.revokeObjectURL(url),
        });
        image.onerror = () => {
            URL.revokeObjectURL(url);
            reject(new Error('Nie udało się odczytać obrazu.'));
        };
        image.src = url;
    });
}

function decodeImage(file) {
    if (typeof createImageBitmap !== 'function') return loadImage(file);
    return createImageBitmap(file, { imageOrientation: 'from-image' })
        .catch(() => createImageBitmap(file))
        .then((bitmap) => ({
            source: bitmap,
            width: bitmap.width,
            height: bitmap.height,
            cleanup: () => { if (typeof bitmap.close === 'function') bitmap.close(); },
        }))
        .catch(() => loadImage(file));
}

function drawCanvas(source, sourceWidth, sourceHeight, maxDimension) {
    const scale = Math.min(1, maxDimension / Math.max(sourceWidth, sourceHeight));
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(sourceWidth * scale));
    canvas.height = Math.max(1, Math.round(sourceHeight * scale));
    const context = canvas.getContext('2d', { alpha: false });
    if (!context) throw new Error('Ta przeglądarka nie może przygotować zdjęcia.');
    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, canvas.width, canvas.height);
    context.drawImage(source, 0, 0, canvas.width, canvas.height);
    return canvas;
}

function shrinkCanvas(canvas, factor) {
    return drawCanvas(
        canvas,
        canvas.width,
        canvas.height,
        Math.max(1, Math.round(Math.max(canvas.width, canvas.height) * factor))
    );
}

function canvasToWebp(canvas, quality) {
    return new Promise((resolve, reject) => {
        if (typeof canvas.toBlob !== 'function') {
            reject(new Error('Ta przeglądarka nie obsługuje zmniejszania zdjęć.'));
            return;
        }
        canvas.toBlob((blob) => {
            if (!blob || blob.type !== 'image/webp') {
                reject(new Error('Ta przeglądarka nie może utworzyć zdjęcia WebP.'));
                return;
            }
            resolve(blob);
        }, 'image/webp', quality);
    });
}

async function encodeWithinLimit(canvas, targetBytes, resizeAttempt = 0) {
    let lastBlob = null;
    for (const quality of WEBP_QUALITY_STEPS) {
        lastBlob = await canvasToWebp(canvas, quality);
        if (lastBlob.size <= targetBytes) {
            return lastBlob;
        }
    }
    if (resizeAttempt >= 4) return lastBlob;
    const estimated = Math.sqrt(targetBytes / Math.max(1, lastBlob.size)) * 0.94;
    return encodeWithinLimit(shrinkCanvas(canvas, Math.max(0.68, Math.min(0.86, estimated))), targetBytes, resizeAttempt + 1);
}

function originalEntry(file, note = '') {
    return {
        file,
        previewUrl: note === '' ? URL.createObjectURL(file) : '',
        converted: false,
        originalSize: file.size,
        uploadSize: file.size,
        processingNote: note,
    };
}

async function prepareListingImage(file, options = {}) {
    const targetBytes = Math.max(262144, Number(options.targetBytes) || DEFAULT_TARGET_BYTES);
    const maxDimension = Math.max(800, Number(options.maxDimension) || DEFAULT_MAX_DIMENSION);
    const uploadMaxBytes = Math.max(0, Number(options.uploadMaxBytes) || 0);
    try {
        const decoded = await decodeImage(file);
        const shouldConvert = isHeicFile(file)
            || file.size > targetBytes
            || Math.max(decoded.width, decoded.height) > maxDimension;
        if (!shouldConvert) {
            decoded.cleanup();
            return originalEntry(file);
        }

        let canvas;
        try {
            canvas = drawCanvas(decoded.source, decoded.width, decoded.height, maxDimension);
        } finally {
            decoded.cleanup();
        }
        const blob = await encodeWithinLimit(canvas, targetBytes);
        if (blob.size > targetBytes || (uploadMaxBytes > 0 && blob.size > uploadMaxBytes)) {
            throw new Error('Nie udało się wystarczająco zmniejszyć zdjęcia.');
        }
        const converted = new File([blob], webpFileName(file.name), {
            type: 'image/webp',
            lastModified: file.lastModified || Date.now(),
        });
        return {
            file: converted,
            previewUrl: URL.createObjectURL(converted),
            converted: true,
            originalSize: file.size,
            uploadSize: converted.size,
            processingNote: '',
        };
    } catch (error) {
        if (isHeicFile(file) && (uploadMaxBytes <= 0 || file.size <= uploadMaxBytes)) {
            return originalEntry(file, 'Podgląd pojawi się po przetworzeniu zdjęcia przez serwer.');
        }
        throw error;
    }
}

module.exports = {
    DEFAULT_MAX_DIMENSION,
    DEFAULT_TARGET_BYTES,
    isHeicFile,
    isSupportedImageFile,
    prepareListingImage,
    webpFileName,
};
